import React, { FormEvent, useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CDataTable,
  CFormGroup,
  CInputCheckbox,
  CLink,
  CModal,
  CPagination,
  CSelect,
  CLabel,
  CInput,
  CModalHeader,
  CModalBody,
  CModalTitle,
} from "@coreui/react";
import {
  Box,
  useToast,
  HStack,
  useDisclosure,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Image,
} from "@chakra-ui/react";
import { FaImage } from "react-icons/fa";
import { isEmpty } from "lodash";
import { useLocation } from "react-router-dom";
import { perPageOptions } from "config";
import { deleteMethod, put, MEDIA_URL } from "api";
import { PaginatedTableProps, SortValue } from "./types";

type SubscriptionType = "sal125" | "ask-salah" | "sal-bioflow";

type ActiveSubscriptionOption = {
  type: SubscriptionType;
  subscription: any;
  label: string;
  colorScheme: string;
};

const hasValidSubscription = (subscription: any) =>
  !!(
    subscription &&
    (subscription.id || subscription.endDate)
  );

const getActiveSubscriptionOptions = (item: any): ActiveSubscriptionOption[] => {
  const options: ActiveSubscriptionOption[] = [];

  if (hasValidSubscription(item.activeSubscription)) {
    options.push({
      type: "sal125",
      subscription: item.activeSubscription,
      label: "SAL125 Subscription",
      colorScheme: "blue",
    });
  }

  if (hasValidSubscription(item.activeAskSalahSubscription)) {
    options.push({
      type: "ask-salah",
      subscription: item.activeAskSalahSubscription,
      label: "Ask Salah Subscription",
      colorScheme: "green",
    });
  }

  if (hasValidSubscription(item.activeBioflowSubscription)) {
    options.push({
      type: "sal-bioflow",
      subscription: item.activeBioflowSubscription,
      label: "SalBioflow Subscription",
      colorScheme: "purple",
    });
  }

  return options;
};

const PaginatedTable: React.FC<PaginatedTableProps> = ({
  fields,
  items,
  perPage,
  activePage,
  pagination,
  select,
  unselect,
  selected,
  setPerPage,
  setActivePage,
  actions,
  sortBy,
  sortValue,
  user,
  setCurrent,
  total,
  scopedSlots = {},
  resolveOrderEndpoint = "subscription/order", // Default to SAL125 endpoint
}) => {
  const location = useLocation();

  //method for accepting and rejecting orders
  const resolveOrder = async (id: number, status: boolean) => {
    put(`${resolveOrderEndpoint}/${id}`, { status }, undefined, user?.accessToken)
      .then((res: any) => {
        if (res && res.id) {
          toast({
            title: "Resolved successfully!",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
        }
        setCurrent && setCurrent(id);
      })
      .catch(() => {
        toast({
          title: "Something went wrong..",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };
  //control the visibility for the edit modal
  const [modalInfo, setModalInfo] = useState<any>({
    item: {},
    visible: false,
    itemId: -1,
  });

  //toasting
  const toast = useToast();

  // new End subscription date
  const [endSubDate, setEndSubDate] = useState("");
  // Subscription type selection
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<SubscriptionType | null>(null);
  const [showSubscriptionTypeModal, setShowSubscriptionTypeModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'edit' | 'cancel' | null>(null);
  const [pendingItem, setPendingItem] = useState<any>(null);

  const resetSubscriptionTypeModal = () => {
    setShowSubscriptionTypeModal(false);
    setPendingAction(null);
    setPendingItem(null);
    setSelectedSubscriptionType(null);
  };

  const openEditModal = (item: any, subscription: any, subscriptionType: SubscriptionType) => {
    subscription?.endDate &&
      setEndSubDate(new Date(subscription.endDate).toJSON().slice(0, 10));
    setSelectedSubscriptionType(subscriptionType);
    setModalInfo({
      item,
      itemId: item.id,
      visible: true,
    });
  };

  const handleSubscriptionAction = (
    item: any,
    action: "edit" | "cancel",
    option?: ActiveSubscriptionOption
  ) => {
    const activeOptions = getActiveSubscriptionOptions(item);

    if (activeOptions.length === 0) {
      return;
    }

    if (activeOptions.length > 1 && !option) {
      setPendingAction(action);
      setPendingItem(item);
      setShowSubscriptionTypeModal(true);
      return;
    }

    const selectedOption = option ?? activeOptions[0];

    if (action === "edit") {
      openEditModal(item, selectedOption.subscription, selectedOption.type);
      return;
    }

    cancelSubscription(selectedOption.subscription);
  };

  const updateUserSubscription = () => {
    const endpoint = `user/${modalInfo.itemId}/updateSubscription`;
    if (!endSubDate) {
      toast({
        title: "Please select a subscription end date.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    // Use selectedSubscriptionType or default to sal125
    const subscriptionType = selectedSubscriptionType || 'sal125';
    
    const body = {
      endDate: endSubDate,
      subscriptionType: subscriptionType,
    };

    console.log('Updating subscription - endpoint:', endpoint);
    console.log('Updating subscription - body:', body);
    console.log('Updating subscription - subscriptionType:', selectedSubscriptionType);
    
    put(endpoint, body, undefined, user?.accessToken)
      .then((res: any) => {
        console.log('Update subscription response:', res);
        console.log('Response type:', typeof res);
        console.log('Response status:', res?.status);
        console.log('Response status === true:', res?.status === true);
        
        if (res && (res.status === true || res.status === 'true')) {
          toast({
            title: "User's subscription duration updated successfully!",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          setModalInfo({ ...modalInfo, visible: false });
          setSelectedSubscriptionType(null);
          // Refresh the data
          if (setCurrent) {
            setCurrent(modalInfo.itemId);
          }
        } else {
          console.error('Update failed - response:', res);
          console.error('Update failed - res is null?', res === null);
          console.error('Update failed - res is undefined?', res === undefined);
          throw new Error("Update failed");
        }
      })
      .catch((error) => {
        console.error('Update subscription error:', error);
        console.error('Error type:', typeof error);
        console.error('Error message:', error?.message);
        toast({
          title: "Couldn't change user subscription duration..",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setModalInfo({ ...modalInfo, visible: false });
        setSelectedSubscriptionType(null);
      });
  };

  const cancelSubscription = async (activeSubscription: any) => {
    if (!activeSubscription || !activeSubscription.id) {
      toast({
        title: "Error",
        description: "Invalid subscription data",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const endpoint = `subscription/${activeSubscription.id}`;
    
    try {
      await deleteMethod(endpoint, user?.accessToken);
      toast({
        title: "Subscription cancelled successfully!",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      // Refresh the data
      if (setCurrent) {
        setCurrent(activeSubscription.id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [previewData, setPreviewData] = useState<{
    src: string | undefined;
    alt: string | undefined;
  }>({ src: undefined, alt: undefined });

  const [pdfBlobUrl, setPdfBlobUrl] = useState("");

  const fetchPdfWithToken = async (pdfUrl: string) => {
    const response = await fetch(pdfUrl, {
      headers: {
        Authorization: `Bearer ${user?.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch PDF");
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    setPdfBlobUrl(blobUrl);
  };

  return (
    <CCard>
      {selected ? (
        <CCollapse show={!isEmpty(selected)}>
          <CCardHeader>
            {actions}
            <small
              style={{
                float: "right",
              }}
            >
              {Object.keys(selected).length} Selected
            </small>
          </CCardHeader>
        </CCollapse>
      ) : (
        <></>
      )}
      <CCardBody>
        {pagination ? (
          <CSelect
            style={{ width: 200, marginInlineStart: "auto" }}
            className="my-2"
            value={perPage}
            onChange={(e) => setPerPage?.(e.currentTarget.value)}
          >
            {perPageOptions.map((p, idx) => (
              <option key={idx} value={p}>
                {p}
              </option>
            ))}
          </CSelect>
        ) : null}
        <CDataTable
          fields={fields}
          scopedSlots={{
            ...scopedSlots,
            _select: (item: any) => (
              <td>
                <CFormGroup variant="checkbox" className="checkbox">
                  <CInputCheckbox
                    checked={item._selected || false}
                    onChange={(e: any) => {
                      e.target.checked
                        ? select?.(item.id)
                        : unselect?.(item.id);
                    }}
                  />
                </CFormGroup>
              </td>
            ),
            proveUrl: (item: any) => (
              <td>
                <IconButton
                  aria-label="payment-proof"
                  icon={<FaImage size={28} color="#2a94f7" />}
                  onClick={() => {
                    if (item.proveUrl) {
                      setPreviewData({
                        src: `${MEDIA_URL}/${item.proveUrl}`,
                        alt: "payment-proof",
                      });

                      if (item.proveUrl?.endsWith(".pdf")) {
                        fetchPdfWithToken(`${MEDIA_URL}/${item.proveUrl}`);
                      }
                    } else
                      toast({
                        title: "Empty",
                        description: "No payment proof available.",
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                      });
                    onOpen();
                  }}
                  variant="ghost"
                  size={"sm"}
                />
              </td>
            ),
            nationalityProofUrl: (item: any) => (
              <td>
                <IconButton
                  aria-label="nationality-proof"
                  icon={<FaImage size={28} color="#2a94f7" />}
                  onClick={() => {
                    if (item.nationalityProofUrl)
                      setPreviewData({
                        src: `${MEDIA_URL}/${item.nationalityProofUrl}`,
                        alt: "nationality-proof",
                      });
                    else
                      toast({
                        title: "Empty",
                        description: "No nationality proof available.",
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                      });
                    onOpen();
                  }}
                  variant="ghost"
                  size={"sm"}
                />
              </td>
            ),
            _edit: (item: any) => (
              <td>
                <CLink
                  color="primary"
                  // className="btn btn-primary btn-sm"
                  to={`${location.pathname}/${item.id}/edit`}
                >
                  Edit
                </CLink>
              </td>
            ),
            accept: (item: any) => {
              const pending =
                item.status === "Pending" || item.status === "pending";
              const canResolve = pending;
              return (
                <td>
                  {user?.role.name !== "Support user" && (
                    <CButton
                      color="success"
                      onClick={
                        canResolve
                          ? () => resolveOrder(item.id, true)
                          : undefined
                      }
                      disabled={!canResolve}
                    >
                      Accept
                    </CButton>
                  )}
                </td>
              );
            },
            reject: (item: any) => {
              const pending =
                item.status === "Pending" || item.status === "pending";
              const canResolve = pending;
              return (
                <td>
                  {user?.role.name !== "Support user" && (
                    <CButton
                      color="danger"
                      onClick={
                        canResolve
                          ? () => resolveOrder(item.id, false)
                          : undefined
                      }
                      disabled={!canResolve}
                    >
                      Reject
                    </CButton>
                  )}
                </td>
              );
            },
            actions: (item: any) => (
              <td>
                <HStack>
                  {user?.role.name &&
                    ["Admin", "Reviewer"].includes(user.role.name) && (
                      <CButton
                        color="primary"
                        style={{ minWidth: "5.5rem", minHeight: "3.5rem" }}
                        onClick={() => handleSubscriptionAction(item, "edit")}
                        disabled={getActiveSubscriptionOptions(item).length === 0}
                      >
                        Edit
                      </CButton>
                    )}
                  {(user?.role.name === "Admin" ||
                    user?.role.name === "Accountant" ||
                    user?.role.name === "Reviewer") && (
                    <CButton
                      color="danger"
                      style={{ minWidth: "6.35rem" }}
                      onClick={() => handleSubscriptionAction(item, "cancel")}
                      disabled={getActiveSubscriptionOptions(item).length === 0}
                    >
                      Cancel subscription
                    </CButton>
                  )}
                </HStack>
              </td>
            ),
          }}
          sorter={{ external: true }}
          sorterValue={sortValue}
          onSorterValueChange={(sortValue: SortValue) => sortBy?.(sortValue)}
          items={items}
        />
        {modalInfo.visible && (
          <CModal
            show={modalInfo.visible}
            onClose={() => {
              setModalInfo({ ...modalInfo, visible: false });
              setSelectedSubscriptionType(null);
            }}
            style={{
              padding: "20px 10px",
              maxHeight: "500px",
              overflowY: "scroll",
            }}
            className="scrollable"
          >
            <CModalHeader closeButton>
              <CModalTitle style={{ fontWeight: 900 }}>
                Edit User Info
              </CModalTitle>
            </CModalHeader>
            <CModalBody>
              <Box display="block" mb="5">
                <p>
                  Name: <b>{modalInfo.item.name}</b>
                </p>
                <p>
                  Email: <b>{modalInfo.item.email}</b>
                </p>
              </Box>
              <CLabel>Subscription ends at : </CLabel>
              <Box display="flex" justifyContent="space-between" mb="5">
                <CInput
                  style={{ width: "60%" }}
                  type="date"
                  id="newEndSubDate"
                  name="newEndSubDate"
                  value={endSubDate}
                  onChange={(e: FormEvent) => {
                    const target = e.target as HTMLInputElement;
                    setEndSubDate(target.value);
                  }}
                ></CInput>
                <CButton
                  color="success"
                  size={"md"}
                  type={"submit"}
                  onClick={() => {
                    updateUserSubscription();
                  }}
                >
                  Update
                </CButton>
              </Box>
            </CModalBody>
          </CModal>
        )}
        {pagination ? (
          <div className="d-flex justify-content-center">
            <CPagination
              className="my-2"
              pages={total}
              activePage={activePage}
              onActivePageChange={(e: any) => {
                setActivePage?.(e);
              }}
            />
          </div>
        ) : null}
      </CCardBody>
      {previewData.src && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            setPreviewData({ src: undefined, alt: undefined });
            onClose();
          }}
          isCentered
          closeOnEsc
          size={previewData.src.endsWith(".pdf") ? "5xl" : undefined}
        >
          <ModalOverlay />
          <ModalContent backgroundColor="transparent" boxShadow="none">
            {previewData.src.endsWith(".pdf") ? (
              <iframe
                src={pdfBlobUrl}
                width="100%"
                height="650px"
                title="PDF Preview"
              ></iframe>
            ) : (
              <Image
                src={previewData.src}
                alt={previewData.alt}
                loading="eager"
                maxH={"650px"}
                objectFit="contain"
              />
            )}
          </ModalContent>
        </Modal>
      )}
      {/* Subscription Type Selector Modal */}
      <Modal
        isOpen={showSubscriptionTypeModal}
          onClose={resetSubscriptionTypeModal}
        isCentered
        closeOnOverlayClick={true}
      >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Select Subscription Type</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <p style={{ marginBottom: '1rem' }}>
                This user has multiple active subscriptions. Which one would you like to {pendingAction}?
              </p>
              <HStack spacing={4} flexWrap="wrap">
                {pendingItem &&
                  getActiveSubscriptionOptions(pendingItem).map((option) => (
                    <Button
                      key={option.type}
                      colorScheme={option.colorScheme}
                      onClick={() => {
                        setSelectedSubscriptionType(option.type);
                        setShowSubscriptionTypeModal(false);

                        if (pendingAction && pendingItem) {
                          handleSubscriptionAction(
                            pendingItem,
                            pendingAction,
                            option
                          );
                        }

                        setPendingAction(null);
                        setPendingItem(null);
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
              </HStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={resetSubscriptionTypeModal}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
    </CCard>
  );
};
export default PaginatedTable;
